import {
  reduce, some, uniqBy, map,
  sumBy, toArray, minBy, findIndex,
  filter, uniq, orderBy, uniqueId,
  findKey, round, min,
} from 'lodash';
import { OrderTypes, ProfileTypes, TicketStatuses } from '@sda/enums';
import { rfc3986EncodeURIComponent } from '@sda/utils/url';
import type { PublicShow } from '@sda/interfaces/show';
import type { PublicPendingProfile } from '@sda/interfaces/profile';
import type { PublicAct } from '@sda/interfaces/act';
import type { GenericList, OptionalUnionTriple } from '@sda/helpers/helpers.types';
import type {
  GuestItem, GuestItemList, ShowSplit, TicketQRCode,
} from './interfaces';
import type { ProductList, Product } from '@sda/interfaces/product';
import type { Ticket, TicketList } from '@sda/interfaces/ticket';
import type { Thread } from '@sda/interfaces/thread';
import type { CurrentSKUAmountList, SKUProduct, SKUPriceList } from '@sda/interfaces/reducer/stripe.reducer';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { PublicVenue } from '@sda/interfaces/venue';
import type { ShowOrderMetadata } from '@sda/interfaces/order';

/**
 * Takes a product list and reduces it to a map of products and their capacities
 * @param {object} show
 * @returns {object} CurrentSKUAmountList
*/
export const generateCurrentSKUAmounts = (products: SKUProduct[]): CurrentSKUAmountList => {
  const currentSKUAmount = reduce(products, (results, product) => {
    if (results[product.id]) {
      return results;
    }

    return {
      ...results,
      [product.id]: product.capacity,
    };
  }, {});

  return currentSKUAmount;
};

/**
 * Takes a product list and reduces it to a map of products and their prices
 * @param {object} show
 * @returns {object} SKUPriceList
*/
export const generateCurrentSKUProductPricing = (products: SKUProduct[]): SKUPriceList => {
  const currentSKUProducts = reduce(products, (results, product) => {
    if (results[product.id]) {
      return results;
    }

    return {
      ...results,
      [product.id]: product.price || product.suggestedPrice || product.minimumPrice || 0,
    };
  }, {});

  return currentSKUProducts;
};

/**
 * Returns true if a single product has a minimumPrice greater than 0
 * @param products
 * @returns boolean
 */
export const hasPaidProduct = (products: ProductList | Product[]): boolean => some(
  products,
  (product: Product) => product.isActive && product.minimumPrice > 0,
);

/**
 * Returns true if a single product in the list is free
 * @param products
 * @returns boolean
 */
export const hasFreeProduct = (products: ProductList | Product[]): boolean => some(
  products,
  (product: Product) => product.isActive && product.minimumPrice <= 0,
);

/**
 * Totals the capacity of all products
 */
export const totalProductCapacity = (products: ProductList | Product[]): number => {
  const totalCapacity = sumBy(toArray(products), (product: Product) => (product.isActive && product.capacity > 0 ? product.capacity : 0));

  return totalCapacity;
};

/**
 * Totals the numTicketsPurchased of all products
 */
export const totalProductPurchased = (products: ProductList | Product[]): number => {
  const totalCapacity = sumBy(toArray(products), (product: Product) => (product.isActive && (product.numTicketsPurchased || 0)));

  return totalCapacity;
};

/**
 * Create a list of guests with ticket quantity based on an array of tickets
 *
 * @param  {any} tickets
 * @return {any[]}
 */
export const generateGuestList = (tickets: TicketList): GuestItem[] => {
  const ticketArray = toArray(reduce<TicketList, GuestItemList>(
    tickets,
    (result, ticket: Ticket & FetchDataType) => {
      if (ticket.status !== TicketStatuses.PURCHASED && ticket.status !== TicketStatuses.COMPED && ticket.status !== TicketStatuses.PASS) {
        return result;
      }

      const label = ticket.guestName || ticket.guestEmail;
      const index = findKey(result, (guest) => guest.email === label || guest.name === label);

      const isCompedTicket = ticket.status === TicketStatuses.COMPED;
      const compedProductId = ticket.description || 'comped';
      const productId = isCompedTicket ? compedProductId : ticket.stripeSKU;

      if (!index) {
        return {
          ...result,
          [uniqueId()]: {
            name: label,
            owner: ticket.owner,
            email: ticket.guestEmail,
            quantity: 1,
            quantityRedeemed: ticket.isRedeemed ? 1 : 0,
            compRedeemed: ticket.pendingRedemption === false ? 1 : 0,
            id: ticket.id,
            containsDoorSale: ticket.stripeOrderType === OrderTypes.ORDER_TYPE_DOOR,
            tickets: [ticket],
            products: {
              [productId]: {
                isCompedTicket,
                description: ticket.description || 'Guest',
                quantity: 1,
                quantityRedeemed: ticket.isRedeemed ? 1 : 0,
              },
            },
          },
        };
      }

      const item = result[index];
      item.tickets.push(ticket);

      const product = item.products[productId] || {
        quantity: 0,
        quantityRedeemed: 0,
      };

      return {
        ...result,
        [index]: {
          ...item,
          name: label,
          owner: ticket.owner,
          email: ticket.guestEmail,
          quantity: item.quantity + 1,
          quantityRedeemed: ticket.isRedeemed ? item.quantityRedeemed + 1 : item.quantityRedeemed,
          compRedeemed: ticket.pendingRedemption === false ? item.compRedeemed + 1 : item.compRedeemed,
          id: ticket.id,
          containsDoorSale: ticket.stripeOrderType === OrderTypes.ORDER_TYPE_DOOR ? true : item.containsDoorSale,
          tickets: item.tickets,
          products: {
            ...item.products,
            [productId]: {
              ...product,
              description: ticket.description || 'Guest',
              isCompedTicket,
              quantity: product.quantity + 1,
              quantityRedeemed: ticket.isRedeemed ? product.quantityRedeemed + 1 : product.quantityRedeemed,
            },
          },
        },
      };
    },
    {},
  ));

  return orderBy(ticketArray, 'name');
};

/**
 * Drag and drop sort helper
 */
export const sortProductsBySortOrder = (
  products: Partial<Product & FetchDataType>[],
  oldId: string,
  newId: string,
): GenericList<Product & FetchDataType> => {
  const dragIndex = findIndex(products, (item) => item.id === oldId);
  const hoverIndex = findIndex(products, (item) => item.id === newId);

  const updatedProducts = reduce(products, (results, product, index) => {
    let { sortOrder } = product;

    if (index === hoverIndex) {
      sortOrder = dragIndex;
    }

    if (index === dragIndex) {
      sortOrder = hoverIndex;
    }

    return {
      ...results,
      [product.id]: {
        ...product,
        sortOrder,
      },
    };
  }, {});

  return updatedProducts;
};

/**
 * Gets the show split values for a given show
 * @return {ShowSplit} - side door and remainder in decimal format
 */
export const getShowSplit = (splitDecimal?: number): ShowSplit => {
  const sidedoorSplitDecimal = splitDecimal || 0;

  return {
    sidedoorSplitDecimal,
    splitRemainderDecimal: 1 - sidedoorSplitDecimal,
  };
};

/**
 * Takes an array of tickets and returns an array of QRCode images
 * @param  {string[]} tickets
 * @return {TicketQRCode} an array on tickets including a label and src
 */
export const getTicketQRCodes = (tickets: { ticketId: string, productName: string }[]): TicketQRCode[] => {
  if (!tickets || !tickets.length) {
    return null;
  }

  let guest = 0;

  const ticketQRCodes = map(tickets, ({ ticketId: ticket, productName: ticketTypeName }) => {
    guest += 1;

    return {
      ticketTypeName,
      src: `${process.env.USER_BUCKET_BASE_URL}/${rfc3986EncodeURIComponent(`qr/tickets/${ticket}`)}.png?alt=media`,
      label: `Ticket ${guest}`,
    };
  });

  return ticketQRCodes;
};

/**
 * Takes a thread and returns a list of user ides used for notifications
 * @param {Thread} thread
 * @param {string} exclude - userId to exclude
 * @return {string[]}
 */
export const getThreadUsersToNotify = (thread: Pick<Thread, 'collaborators'>, exclude?: string): string[] => {
  if (!thread) {
    return [];
  }

  let otherUsers = thread.collaborators || [];

  if (exclude) {
    otherUsers = filter(otherUsers, (userId) => userId !== exclude);
  }

  otherUsers = uniq(otherUsers);

  return otherUsers;
};

export const getNormalizedProfile = <
T extends OptionalUnionTriple<PublicAct, PublicVenue, PublicPendingProfile> = OptionalUnionTriple<PublicAct, PublicVenue, PublicPendingProfile>,
>(
    profileType: ProfileTypes,
    profile: T & FetchDataType,
  ) => {
  if (profileType === ProfileTypes.VENUE) {
    return {
      ...profile,
      profileType: ProfileTypes.VENUE,
    };
  }

  if (profileType === ProfileTypes.PENDING) {
    return {
      ...profile,
      publicImageUrl: [],
      name: profile.fullName || '',
      profileType: ProfileTypes.PENDING,
    } as any as T & FetchDataType;
  }

  return {
    ...profile,
    description: profile.bioHTML,
    profileType: ProfileTypes.ACT,
    hasPA: profile.requiresPA,
    isPresenter: false,
    isVetted: true,
  };
};

/**
 * Returns whether or not the given profile responds quickly to show calls
 * @param  {PublicAct} data
 * @return {boolean}
 */
export const getProfileRespondsQuickly = (data: Pick<PublicAct, 'averageConnectionTime' | 'numConnections'>): boolean => {
  const { averageConnectionTime, numConnections } = data;

  if (!numConnections || numConnections < 2) {
    return false;
  }

  if (!averageConnectionTime || averageConnectionTime > 86400) {
    return false;
  }

  return true;
};

/**
 * Generates a PricingInformation object given a list of Products
 * @param  {Product[]} products
 */
export const generatePricingInformation = (products: Pick<Product, 'minimumPrice' | 'hasVariablePrice' | 'isActive'>[]) => {
  const filteredProducts = filter(products, (product) => product.isActive);
  const uniqueProducts = uniqBy(filteredProducts, (product) => `${product.minimumPrice}-${product.hasVariablePrice ? 'true' : 'false'}`);

  const minPriceProduct = minBy(uniqueProducts, (product) => product.minimumPrice);

  return {
    minimumPrice: minPriceProduct?.minimumPrice || 0,
    hasVariablePrice: uniqueProducts.length > 1 || minPriceProduct?.hasVariablePrice || false,
  };
};

export const generateShowMetadataCallback = (show: PublicShow, metadata: ShowOrderMetadata) => {
  let totalSplitPercent = show.sidedoorSplitPercent;
  totalSplitPercent += sumBy(show.publicProfiles, (profileData) => (profileData.profileCollectsTax ? profileData.profileSplitPercent : 0));

  let averageTaxRate = 0;

  if (metadata?.netTotal === 0 || totalSplitPercent === 0) {
    averageTaxRate = 0;
  } else {
    averageTaxRate = round(((metadata?.taxTotal || 0 / totalSplitPercent) / (metadata?.netTotal || 1)), 2);
  }

  // For in-person shows, the tax rate should always be equal to the show's tax rate, regardless of where the purchaser is
  if (averageTaxRate === 0 || !show.isOnlineOnly) {
    averageTaxRate = show.taxRate || 0;
  }

  return (profileId?: string) => {
    const profile = show.publicProfiles.find((item) => item.profileId === profileId);

    if (!profile) {
      return {
        profileId: null,
        profileType: null,
        profileCollectsTax: true,
        profileSplitPercent: show.sidedoorSplitPercent,
        gross: min([(metadata?.gross || 0) - (metadata?.stripeFee || 0), metadata?.sidedoorGross]),
        total: metadata?.sidedoorTotal,
        taxRate: (show.country === 'CA' || metadata?.sidedoorTaxTotal) ? averageTaxRate : 0,
        taxTotal: metadata?.sidedoorTaxTotal,
      };
    }

    const taxRate = profile.profileCollectsTax ? averageTaxRate : 0;

    if (!show.hasMultiPartySupport) {
      if (profile.profileType === ProfileTypes.ACT) {
        return {
          ...profile,
          taxRate,
          gross: metadata?.artistGross,
          total: metadata?.artistTotal,
          taxTotal: metadata?.artistTaxTotal,
        };
      }

      if (profile.profileType === ProfileTypes.VENUE) {
        return {
          ...profile,
          taxRate,
          gross: metadata?.hostGross,
          total: metadata?.hostTotal,
          taxTotal: metadata?.hostTaxTotal,
        };
      }
    }

    if (profile.profileId === show.ownerProfileId) {
      return {
        ...profile,
        taxRate,
        gross: metadata?.artistGross,
        total: metadata?.artistTotal,
        taxTotal: metadata?.artistTaxTotal,
      };
    }

    return {
      ...profile,
      taxRate,
      gross: metadata?.hostGross,
      total: metadata?.hostTotal,
      taxTotal: metadata?.hostTaxTotal,
    };
  };
};
